import { ChangeDetectorRef, DestroyRef, Directive, ElementRef, inject, Input } from '@angular/core';
import { WebCashPermissionKeys } from '../../../../core/enums/WebCashPermissionKeys';
import {
  PermissionService,
} from '../../../../features/auth/core/services/PermissionService';
import { EmployeesStore } from '../../../../features/employees/core/stores/EmployeesStore';
import { EcPrintLocationParamValues } from '../../../../features/terminals/core/enums/EcPrintLocationParamValues';
import { MandatorParameterKeys } from '../../../../features/terminals/core/enums/MandatorParameterKeys';
import { TerminalParameterKeys } from '../../../../features/terminals/core/enums/TerminalParameterKeys';
import { SumUpDevReader } from '../../../../features/terminals/core/sumup-dev-reader';
import { TerminalsStore } from '../../../../features/terminals/core/stores/TerminalsStore';
import { NotificationService } from '../../../services/NotificationService';
import { LanguageStore } from '../../../stores/LanguageStore';

@Directive()
export abstract class BaseComponent {

  @Input() set namespace(namespace: string) {
    this._namespace = namespace;
    this.cdr.markForCheck();
  };

  public readonly languageConfig = inject(LanguageStore).languageConfig();
  public readonly terminalsStore = inject(TerminalsStore);
  public readonly employeesStore = inject(EmployeesStore);
  public readonly cdr = inject(ChangeDetectorRef);
  public readonly isCustomerScreen = new URLSearchParams(location.search)
    .get('isCustomerScreen') === 'true';
  public readonly terminalParametersMap = this.terminalsStore.terminalParametersMap;
  public readonly systemParametersMap = this.terminalsStore.systemParametersMap;
  public readonly mandatorParametersMap = this.terminalsStore.mandatorParametersMap;
  public readonly terminalParameterKeys = TerminalParameterKeys;
  public readonly mandatorParameterKeys = MandatorParameterKeys;
  public readonly destroyRef = inject(DestroyRef);
  public readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  protected readonly permissionService = inject(PermissionService);
  public readonly permissionKeys = WebCashPermissionKeys;

  protected readonly notificationService = inject(NotificationService);

  private _namespace: string;

  public get namespace(): string {
    return this._namespace;
  }

  public get isPreviewMode(): boolean {
    return this.terminalsStore.selectedTerminal()?.currentSession.employee.id !== this.employeesStore.myEmployee()?.id;
  }

  public get isReservationTerminal(): boolean {
    return this.terminalsStore.selectedTerminal()?.flagReservation;
  }

  public get isOtsTerminal(): boolean {
    return !!this.terminalsStore.selectedTerminal()?.flagOts;
  }

  public get isTseConnected(): boolean {
    return !!this.terminalsStore.selectedTerminal()?.tseClientId;
  }

  public get isTerminalWithoutSafe(): boolean {
    return !!!this.terminalsStore.selectedTerminal()?.currentSession?.safeNr;
  }

  public get isEcStandalone(): boolean {
    return this.terminalParametersMap()[TerminalParameterKeys.CASH_EC_STANDALONE_TERMINAL] === true;
  }

  public get isEcSumUp(): boolean {
    return String(this.terminalParametersMap()[TerminalParameterKeys.CASH_EC_LIBRARY] ?? '').trim() === '3';
  }

  public get isSumUpCardBlocked(): boolean {
    SumUpDevReader.revision();
    if (!this.isEcSumUp) {
      return false;
    }

    const configuredReader = String(
      this.terminalParametersMap()[TerminalParameterKeys.CASH_EC_SUMUP_READER_ID] ?? ''
    ).trim();
    return !configuredReader && !SumUpDevReader.readerId();
  }

  // EC terminal prints payment slips on the POS/Epson (worker buffers them) when set to Pos;
  // otherwise the EC terminal prints on its own device. Worker mapping: 0 => Pos, 1 => Terminal.
  public get isEcPrintLocationPos(): boolean {
    return this.terminalParametersMap()[TerminalParameterKeys.CASH_EC_PRINT_LOCATION]
      === EcPrintLocationParamValues.POS;
  }
}
